import { useEffect, useState } from "react";
import type { FloodGauge } from "@yala/shared";

/**
 * useGaugeHistory — accumulate the last `windowHours` of FloodDash gauge
 * observations in localStorage so the river profile can show real sparklines
 * ("last 6h", "last 24h") instead of single points.
 *
 * Behaviour:
 *  - First load: nothing in storage → empty series, panel shows "collecting"
 *  - On every gauge refresh (FloodDash updates ~hourly): the new reading is
 *    pushed into the per-gauge ring buffer
 *  - The hook is purely additive: it never deletes data, just expires entries
 *    older than `windowHours` on each read
 *
 * The shape is a Record<gaugeId, Array<{t: ISO, levelM: number}>> keyed by the
 * FloodDash gauge id, so different gauge name variants (บ้านท่าสาบ / บ้านท่าสาป)
 * each get their own track. If two readings collide within 1 min, the newer one
 * overwrites — keeps the ring buffer dense without spamming storage.
 */

const STORAGE_KEY = "yala:gauge-history-v1";
const DEFAULT_WINDOW_HOURS = 24;
const MIN_INTERVAL_MS = 60_000;

export interface GaugePoint {
  t: string; // ISO
  levelM: number | null;
  status: FloodGauge["status"];
}

export type GaugeHistory = Record<string, GaugePoint[]>;

/* ── Pure helpers — exported for unit tests ─────────────────────────────────
   These read/write localStorage; the hook glues them to React. Tests run in
   `node` env and stub localStorage via vitest's happy-dom or just import. */

export function readHistoryFromStorage(
  storage: Pick<Storage, "getItem"> | undefined | null,
): GaugeHistory {
  if (!storage) return {};
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as GaugeHistory;
    if (typeof parsed !== "object" || parsed === null) return {};
    return parsed;
  } catch {
    return {};
  }
}

export function writeHistoryToStorage(
  storage: Pick<Storage, "setItem"> | undefined | null,
  h: GaugeHistory,
): void {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(h));
  } catch {
    // Safari private mode, quota exceeded — silently skip. Sparkline degrades
    // to a single point, the rest of the panel still works.
  }
}

export function trimHistory(h: GaugeHistory, windowHours: number): GaugeHistory {
  const cutoff = Date.now() - windowHours * 60 * 60 * 1000;
  const out: GaugeHistory = {};
  for (const [id, pts] of Object.entries(h)) {
    const trimmed = pts.filter((p) => new Date(p.t).getTime() >= cutoff);
    if (trimmed.length > 0) out[id] = trimmed;
  }
  return out;
}

export function dedupAndPush(series: GaugePoint[], next: GaugePoint): GaugePoint[] {
  if (series.length === 0) return [next];
  const last = series[series.length - 1];
  if (Date.parse(next.t) - Date.parse(last.t) < MIN_INTERVAL_MS) {
    return [...series.slice(0, -1), next];
  }
  return [...series, next];
}

/** Merge the latest reading from a gauge into the per-gauge history. */
export function mergeGauge(
  history: GaugeHistory,
  gauge: FloodGauge,
): { history: GaugeHistory; changed: boolean } {
  if (!gauge.id || gauge.levelM == null) return { history, changed: false };
  const series = history[gauge.id] ?? [];
  const merged = dedupAndPush(series, {
    t: gauge.observedAt || new Date().toISOString(),
    levelM: gauge.levelM,
    status: gauge.status,
  });
  if (merged === series) return { history, changed: false };
  return { history: { ...history, [gauge.id]: merged }, changed: true };
}

export function useGaugeHistory(
  gauges: FloodGauge[],
  windowHours = DEFAULT_WINDOW_HOURS,
): { history: GaugeHistory; seriesCount: number } {
  const [history, setHistory] = useState<GaugeHistory>(() =>
    readHistoryFromStorage(typeof window === "undefined" ? null : window.localStorage),
  );

  // When new gauge data arrives, append the latest reading for each gauge.
  // The FloodDash adapter already de-dupes upstream, so we just trust the
  // most recent value per gauge per refresh.
  useEffect(() => {
    if (gauges.length === 0) return;
    const storage = typeof window === "undefined" ? null : window.localStorage;
    const current = readHistoryFromStorage(storage);
    let next = current;
    let changed = false;
    for (const g of gauges) {
      const r = mergeGauge(next, g);
      next = r.history;
      if (r.changed) changed = true;
    }
    if (!changed) return;
    const trimmed = trimHistory(next, windowHours);
    writeHistoryToStorage(storage, trimmed);
    setHistory(trimmed);
  }, [gauges, windowHours]);

  // Cleanup: drop entries older than the window whenever the hook re-runs.
  useEffect(() => {
    const cleaned = trimHistory(history, windowHours);
    if (Object.keys(cleaned).length !== Object.keys(history).length) {
      const storage = typeof window === "undefined" ? null : window.localStorage;
      writeHistoryToStorage(storage, cleaned);
      setHistory(cleaned);
    }
  }, [history, windowHours]);

  const seriesCount = Object.values(history).reduce(
    (sum, pts) => sum + pts.length,
    0,
  );

  return { history, seriesCount };
}
